<script>
import Icon from '@/components/Icon'
import { parse, format } from 'date-fns'

export default {
  name: 'OrderStatusTracker',

  components: {
    Icon,
  },

  props: {
    currentStatus: {
      type: String,
      default: null,
    },

    statuses: {
      type: Array,
      default: () => [],
    },
  },

  data () {
    return {
      defaultIcons: {
        'Pedido recebido': 'dot-circle',
        'Pedido confirmado': 'clipboard-list-check',
        'Pedido em separação': 'dolly-solid-full',
        'NF Gerada': 'receipt',
        'Saiu para entrega': 'shipping-fast',
        'Pedido entregue': 'check',
        'Pedido cancelado': 'times',
      },
    }
  },

  computed: {
    processedStatuses () {
      return this.statuses.map((status, index) => ({
        ...status,
        icon: this.defaultIcons[status.name] || 'dot-circle',
        isActive: status.name === this.currentStatus,
        isCompleted: status.completed || status.timestamp,
      }))
    },
  },

  mounted () {
  },

  methods: {
    parseTimestamp (timestamp) {
      if (!timestamp) return null

      try {
        let date

        // Verifica se o timestamp tem hora e minutos
        if (timestamp.includes(':')) {
          // Formato: d-m-Y H:i:s (ex: 27-10-2025 08:57:12)
          date = parse(timestamp, 'd-M-yyyy HH:mm:ss', new Date())
        } else {
          // Formato: d-m-Y (ex: 27-10-2025)
          date = parse(timestamp, 'd-M-yyyy', new Date())
        }

        // Verifica se a data é válida
        if (isNaN(date.getTime())) {
          return null
        }

        return date
      } catch (error) {
        return null
      }
    },

    formatDate (timestamp) {
      if (!timestamp) return ''

      // Se o horário é 00:00:00, retorna apenas a data
      if (timestamp.includes('00:00:00')) {
        const date = this.parseTimestamp(timestamp)
        if (!date) return ''
        return format(date, 'dd/MM/yyyy')
      }

      // Se tem hora diferente de 00:00:00, retorna a data
      if (timestamp.includes(':')) {
        const date = this.parseTimestamp(timestamp)
        if (!date) return ''
        return format(date, 'dd/MM/yyyy')
      }

      // Se só tem data, retorna a data formatada
      const date = this.parseTimestamp(timestamp)
      if (!date) return ''
      return format(date, 'dd/MM/yyyy')
    },

    formatTime (timestamp) {
      if (!timestamp) return ''

      // Se o horário é 00:00:00, não retorna hora
      if (timestamp.includes('00:00:00')) {
        return ''
      }

      // Se tem hora diferente de 00:00:00, retorna apenas a hora
      if (timestamp.includes(':')) {
        const date = this.parseTimestamp(timestamp)
        if (!date) return ''
        return format(date, 'HH:mm')
      }

      // Se só tem data, não retorna hora
      return ''
    },

    getStatusClass (status) {
      return {
        [this.$classes['status-item']]: true,
        [this.$classes['status-active']]: status.isActive,
        [this.$classes['status-completed']]: status.isCompleted,
        [this.$classes['status-cancelled']]: status.name === 'Pedido cancelado',
      }
    },
  },
}
</script>

<template>
  <div :class="$classes['order-status-tracker']">
    <div :class="$classes['tracker-container']">
      <div v-for="(status, index) in processedStatuses" :key="status.id" :class="getStatusClass(status)">
        <!-- Ícone do Status -->
        <div :class="$classes['status-icon-wrapper']">
          <div :class="$classes['status-icon']">
            <icon :name="status.icon" />
            <!-- <icon name="check" /> -->
          </div>
        </div>

        <!-- Linha Conectora -->
        <div v-if="index < processedStatuses.length - 1" :class="[$classes['status-line'], {
          [$classes['status-line-completed']]: status.isCompleted && status.name !== 'Pedido cancelado'
        }]" />

        <!-- Informações do Status -->
        <div :class="$classes['status-info']">
          <span :class="$classes['status-name']">{{ status.name }}</span>
          <div v-if="status.timestamp" :class="$classes['status-time-container']">
            <span v-if="formatDate(status.timestamp)" :class="$classes['status-date']">
              {{ formatDate(status.timestamp) }}
            </span>
            <span v-if="formatTime(status.timestamp)" :class="$classes['status-time']">
              {{ formatTime(status.timestamp) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style module>
.order-status-tracker {
  background: #fff;
  padding: 30px;
  width: auto;
  border-radius: 16px 16px 8px 8px;
  margin: 0 32px 24px 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.tracker-container {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
  width: 100%;
}

.status-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 0;
}

.status-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  position: relative;
  z-index: 2;
}

.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #E0E0E0;
  color: #9E9E9E;
  transition: all 0.3s ease;
}

.status-icon .check {
  color: #28a745;
}

.status-item.status-completed .status-icon {
  background-color: #4A94D4;
  color: #fff;
}

.status-item.status-active .status-icon {
  background-color: #1976D2;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.2);
}

.status-item.status-cancelled .status-icon {
  background-color: #D14040;
  color: #fff;
}

.status-line {
  position: absolute;
  top: 24px;
  left: 50%;
  width: 100%;
  height: 2px;
  background-color: #E0E0E0;
  z-index: 1;
  transition: background-color 0.3s ease;
}

.status-line-completed {
  background-color: #4A94D4;
}

.status-item:last-child .status-line {
  display: none;
}

.status-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  width: 100%;
  height: 50px;
  justify-content: space-between;
  padding: 0 8px;
}

.status-name {
  font-size: 13px;
  font-weight: 500;
  color: #757575;
  line-height: 1.3;
  word-wrap: break-word;
  transition: color 0.3s ease;
}

.status-item.status-completed .status-name,
.status-item.status-active .status-name {
  color: #292929;
  font-weight: 600;
}

.status-time-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.status-date {
  font-size: 12px;
  color: #1976D2;
  font-weight: 600;
  margin-top: 2px;
}

.status-time {
  font-size: 12px;
  color: #1976D2;
  font-weight: 600;
}

/* Responsividade */
@media (max-width: 1024px) {
  .order-status-tracker {
    padding: 20px;
    margin: 0 20px 20px 20px;
    min-height: 150px;
  }

  .status-icon {
    width: 40px;
    height: 40px;
  }

  .status-line {
    top: 20px;
  }

  .status-name {
    font-size: 12px;
  }

  .status-date,
  .status-time {
    font-size: 11px;
  }
}

@media (max-width: 768px) {
  .order-status-tracker {
    padding: 16px;
    margin: 0 16px 16px 16px;
    border-radius: 12px 12px 8px 8px;
    overflow-x: auto;
    min-height: 150px;
  }

  .tracker-container {
    min-width: 600px;
  }

  .status-icon {
    width: 36px;
    height: 36px;
  }

  .status-line {
    top: 18px;
  }

  .status-name {
    font-size: 11px;
  }

  .status-date,
  .status-time {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .tracker-container {
    min-width: 500px;
  }

  .status-icon {
    width: 32px;
    height: 32px;
  }

  .status-line {
    top: 16px;
  }
}
</style>
